/**
 * The invitation's CONTENT — the dark ritual-tinted card body, shared by the two surfaces
 * that offer the install.
 *
 * It exists because the founder asked for the dashboard's card on Perfil too, on
 * 2026-08-28, after finding that Perfil's plain steps panel plus a row that only scrolled
 * to it were two things doing one job. Extracting the body rather than copying it is what
 * keeps the two surfaces from drifting: same copy, same steps, same glyphs, same stage.
 *
 * What differs is POLICY, and that stays with each host:
 * - the dashboard gates on `shouldInviteInstall` and offers "Ahora no";
 * - Perfil offers no dismissal at all, because it is the permanent home. A member who
 *   said "ahora no" twice on the dashboard must still find the instructions somewhere,
 *   and this is the somewhere.
 *
 * Pure presentational: it holds the steps' open/closed state and nothing else. The
 * `beforeinstallprompt` and what a decline means belong to the host.
 */
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "~/components/ui";
import { IconArrowRight } from "~/components/layout/nav-icons";
import type { InstallPlatform } from "~/lib/pwa/device";
import type { BeforeInstallPromptEvent } from "~/lib/pwa/install-prompt-store";

interface InstallInviteBodyProps {
  /** Resolved server-side from the User-Agent, so the first paint is already correct. */
  platform: InstallPlatform;
  /** The held native prompt, or `null` — which is every iPhone. */
  deferredPrompt: BeforeInstallPromptEvent | null;
  /** Opens the native dialog. Only ever called when `deferredPrompt` is non-null. */
  onInstall: () => void;
  installing?: boolean;
  /** Given only where a dismissal is meaningful. Omit it and no "Ahora no" renders. */
  onDismiss?: () => void;
}

function IconShare() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M12 3v12M8.5 6.5 12 3l3.5 3.5" />
      <path d="M6 11H5a1 1 0 0 0-1 1v8a1 1 0 0 0 1 1h14a1 1 0 0 0 1-1v-8a1 1 0 0 0-1-1h-1" />
    </svg>
  );
}

function IconPlusSquare() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="4" y="4" width="16" height="16" rx="4" />
      <path d="M12 9v6M9 12h6" />
    </svg>
  );
}

/** The browser menu — step 1 on Android. */
function IconMenu() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" aria-hidden="true">
      <circle cx="12" cy="5.5" r="1.2" fill="currentColor" />
      <circle cx="12" cy="12" r="1.2" fill="currentColor" />
      <circle cx="12" cy="18.5" r="1.2" fill="currentColor" />
    </svg>
  );
}

function IconCheck() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M5 12.5l4.5 4.5L19 7" />
    </svg>
  );
}

/**
 * Glyph per STEP KEY, never per index.
 *
 * Indexed lookup paired the share glyph with "Abre TheFittClub en Safari" and the plus
 * with "Toca el botón Compartir" — every row showing the icon of a different step, which
 * is worse than no icon. Both the design rubric and the comprehension judge caught it
 * independently, the judge through two readers who said the mark contradicted the words:
 * "el icono que le han puesto al lado es un signo de más".
 *
 * Keying by step is necessary but not sufficient: rewriting the iOS copy dropped the
 * "open it in Safari" step, so every remaining row moved up one action and the map had to
 * move with it. A glyph is a claim about what the sentence beside it says — when the
 * sentence changes, re-read the pairing in a browser.
 */
const STEP_ICON: Record<string, () => React.ReactElement> = {
  a2hs_ios_step_1: IconShare,
  a2hs_ios_step_2: IconPlusSquare,
  a2hs_ios_step_3: IconCheck,
  a2hs_android_step_1: IconMenu,
  a2hs_android_step_2: IconPlusSquare,
  a2hs_android_step_3: IconCheck,
  a2hs_desktop_step_1: IconPlusSquare,
  a2hs_desktop_step_2: IconCheck,
};

/**
 * The phone silhouette with the real icon settling into its empty slot.
 *
 * The REAL `/icon-192.png`, not a drawn stand-in — the T52 lesson, learned when a card
 * previewing an install showed a mark that was not the one landing on her home screen.
 * Decorative: the copy beside it carries the meaning.
 */
function InviteStage() {
  return (
    <div className="ii-stage" aria-hidden="true">
      <div className="ii-phone">
        <div className="dots">
          {Array.from({ length: 12 }, (_, i) => (
            <span key={i} className={i === 4 ? "dot is-slot" : "dot"} />
          ))}
        </div>
        {/* The REAL icon, not a drawn stand-in — the T52 lesson: a card previewing an
            install must not preview the wrong mark. Decorative (the stage is aria-hidden);
            the copy beside it carries the meaning.
            The `?v=` stamp is the same one root.tsx and the manifests carry, and it is
            load-bearing for exactly the T52 reason: this file is cached under an unchanged
            URL, so without the bump a returning visitor is shown the PREVIOUS mark in a
            card whose whole job is to show her the one that will land on her home
            screen. */}
        <img
          className="ii-app"
          src="/icon-192.png?v=5"
          alt=""
          width="36"
          height="36"
          decoding="async"
        />
      </div>
    </div>
  );
}

export function InstallInviteBody({
  installing = false,
  platform,
  deferredPrompt,
  onInstall,
  onDismiss,
}: InstallInviteBodyProps) {
  const { t } = useTranslation("pwa");
  const [stepsOpen, setStepsOpen] = useState(false);
  const stepsRef = useRef<HTMLOListElement>(null);

  // iOS never fires `beforeinstallprompt`, and neither does a browser that has not
  // offered one — in both cases the honest affordance is this device's real steps, not a
  // button that would do nothing.
  const showsSteps = platform === "ios" || deferredPrompt === null;
  const stepKeys =
    platform === "android"
      ? (["a2hs_android_step_1", "a2hs_android_step_2", "a2hs_android_step_3"] as const)
      : platform === "desktop"
        ? (["a2hs_desktop_step_1", "a2hs_desktop_step_2"] as const)
        : (["a2hs_ios_step_1", "a2hs_ios_step_2", "a2hs_ios_step_3"] as const);

  /**
   * Escape folds the steps. Registered on the document while they are open rather than as
   * a JSX handler: the steps are an `<ol>`, and a keydown listener on a non-interactive
   * element is one no keyboard can reliably reach. Same shape as `AvatarMenu`'s sheet.
   */
  useEffect(() => {
    if (!stepsOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setStepsOpen(false);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [stepsOpen]);

  function openSteps() {
    setStepsOpen(true);
    // Focus the list itself rather than stealing into its first item: the steps are
    // reading material, not a form. `tabIndex={-1}` makes it a programmatic target only.
    window.requestAnimationFrame(() => stepsRef.current?.focus());
  }

  return (
    <>
      <div className="ii-copy">
        {/* The production atom for exactly this: mono eyebrow, on dark, tinted by the
            active ritual. The prototype spelled the tint as an inline style; here it is a
            class that already exists, so Core and Sculpt theme themselves. */}
        <span className="mono-label accent-ritual-on-dark">{t("invite_eyebrow")}</span>
        <h3 className="ii-title">{t("invite_title")}</h3>
        <p className="ii-body">{t("invite_body")}</p>

        {stepsOpen ? (
          <>
            <ol className="ii-steps" ref={stepsRef} tabIndex={-1}>
              {stepKeys.map((key, i) => {
                const Icon = STEP_ICON[key] ?? IconCheck;
                return (
                  <li key={key}>
                    <span className="n" aria-hidden="true">
                      {i + 1}
                    </span>
                    <Icon />
                    <span>{t(key)}</span>
                  </li>
                );
              })}
            </ol>
            {/* The one thing the retired `AddToHomeScreenPanel` said that the steps do not.
                On iOS Chrome the share sheet has no "add to home screen", so a member
                following these steps in the wrong browser would fail at step 2 with no
                idea why. Stating the limit is what stops that; hiding it only relocates
                the confusion. */}
            {platform === "ios" ? <p className="ii-note">{t("a2hs_ios_note")}</p> : null}
            <div className="ii-actions">
              {/* Folding the steps is NOT a dismissal — she read them, she did not say no. */}
              <Button type="button" variant="text" surface="dark" size="sm" onClick={() => setStepsOpen(false)}>
                {t("invite_steps_done")}
              </Button>
            </div>
          </>
        ) : (
          <div className="ii-actions">
            <Button
              type="button"
              variant="dark"
              surface="dark"
              size="sm"
              isLoading={installing}
              onClick={showsSteps ? openSteps : onInstall}
             trailingIcon={<IconArrowRight size={15} />}>
              {showsSteps ? t("invite_action_steps") : t("invite_action_direct")}

            </Button>
            {onDismiss ? (
              <Button type="button" variant="text" surface="dark" size="sm" onClick={onDismiss}>
                {t("invite_dismiss")}
              </Button>
            ) : null}
          </div>
        )}
      </div>

      {stepsOpen ? null : <InviteStage />}
    </>
  );
}
